import { Flex, Heading, Text } from "@chakra-ui/react";
import { useField } from "formik";
import React from "react";
import { FormikUploadFileInput } from "src/components/Inputs/Formik/FormikUploadFileInput";
import { InputWithValidationControl } from "src/components/Inputs/InputWithValidationControl";
import { EditorRef } from "src/components/WysiwygEditor/lexical/Editor";
import FormikLexicalEditor from "src/components/WysiwygEditor/lexical/FormikEditor";
import { MAX_EMAIL_ATTACHMENTS_SIZE_MB } from "src/constants";
import { AttachmentsList } from "./AttachmentsList";

type Props = {
  hasRecipientsWithEmail: boolean;
  hasRecipientsWithPhone: boolean;
  emailEditorRef: EditorRef;
  smsEditorRef: EditorRef;
};
export const MessageForm: React.FC<Props> = ({
  hasRecipientsWithEmail,
  hasRecipientsWithPhone,
  emailEditorRef,
  smsEditorRef,
}) => {
  const [emailAttachmentsField, , emailAttachmentsHelpers] =
    useField<File[]>("email_attachments");

  return (
    <Flex direction="column" gap={8}>
      {hasRecipientsWithEmail ? (
        <Flex direction="column" gap={2}>
          <Heading as="h2" size="md">
            Email message
          </Heading>
          <InputWithValidationControl
            inputProps={{ placeholder: "Message subject", my: 2 }}
            name="subject"
            id="subject"
            label="Subject line"
            labelProps={{ fontSize: "sm", color: "gray.700" }}
          />
          <FormikLexicalEditor
            name="email_body"
            placeholder="Email content"
            height="300px"
            width="100%"
            label="Content"
            labelProps={{ fontSize: "sm", color: "gray.700" }}
            isRequired={true}
            editorRef={emailEditorRef}
            settings={{
              enableVariables: true,
              variablesContext: {
                type: "adhoc",
              },
            }}
          />
          <AttachmentsList
            attachments={emailAttachmentsField.value}
            onDelete={(idx) => {
              emailAttachmentsHelpers.setValue(
                emailAttachmentsField.value.filter((_, i) => i !== idx)
              );
            }}
          />
          <FormikUploadFileInput
            multiple={true}
            accept="image/jpg, image/jpeg, image/png, .heic, application/pdf"
            buttonLabel="Attach files to email"
            boxProps={{ w: "fit-content" }}
            name="email_attachments"
          />
          <Text fontSize="xs" color="gray" mt={2}>
            You can upload files that don't exceed{" "}
            <b>{MAX_EMAIL_ATTACHMENTS_SIZE_MB}MB total</b>, recommended max size
            is <b>10MB each</b>. Supported formats: JPG/JPEG, PNG, HEIC, PDF.
          </Text>
        </Flex>
      ) : (
        <Text color="blackAlpha.700" fontWeight="400">
          No email recipients
        </Text>
      )}

      <Heading as="h2" size="md">
        SMS message
      </Heading>

      {hasRecipientsWithPhone ? (
        <FormikLexicalEditor
          name="sms_body"
          placeholder="SMS content"
          height="150px"
          width="100%"
          editorVersion="lite"
          editorRef={smsEditorRef}
          settings={{
            enableVariables: true,
            enableSmsStats: true,
            variablesContext: {
              type: "adhoc",
            },
          }}
        />
      ) : (
        <Text color="blackAlpha.700" fontWeight="400">
          No SMS recipients
        </Text>
      )}
    </Flex>
  );
};
